// TimeZonesOverlay component - Handles time zone visualization on the map
import { useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

interface TimeZonesOverlayProps {
  map: google.maps.Map | null;
  isVisible: boolean;
}

interface ZoneLabel {
  tzid: string;
  name: string;
  lat: number;
  lng: number;
}

const DARK_ZONE_COLORS: { [key: string]: string } = {
  "America/Los_Angeles": "#2196F3",
  "America/Denver": "#4CAF50",
  "America/Chicago": "#FFC107",
  "America/New_York": "#FF5722",
};

const LIGHT_ZONE_COLORS: { [key: string]: string } = {
  "America/Los_Angeles": "#E3F2FD",
  "America/Denver": "#E8F5E9",
  "America/Chicago": "#FFF8E1",
  "America/New_York": "#FBE9E7",
};

const ZONE_LABELS: ZoneLabel[] = [
  { tzid: "America/Los_Angeles", name: "PACIFIC", lat: 49.2, lng: -120.2 },
  { tzid: "America/Denver", name: "MOUNTAIN", lat: 49.2, lng: -111.5 },
  { tzid: "America/Chicago", name: "CENTRAL", lat: 49.2, lng: -100.8 },
  { tzid: "America/New_York", name: "EASTERN", lat: 46, lng: -73.6 },
];

export const TimeZonesOverlay: React.FC<TimeZonesOverlayProps> = ({
  map,
  isVisible,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const ZONE_COLORS = isDarkMode ? DARK_ZONE_COLORS : LIGHT_ZONE_COLORS;

  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const [dataLayer, setDataLayer] = useState<google.maps.Data | null>(null);
  const markerLibraryRef = useRef<
    typeof google.maps.marker.AdvancedMarkerElement | null
  >(null);
  const updateIntervalRef = useRef<NodeJS.Timeout>();

  // Function for loading Google Maps library
  const loadMarkerLibrary = useCallback(async () => {
    if (!markerLibraryRef.current) {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary(
        "marker"
      )) as google.maps.MarkerLibrary;
      markerLibraryRef.current = AdvancedMarkerElement;
    }
    return markerLibraryRef.current;
  }, []);

  // Function to get current time for a timezone
  const getCurrentTime = useCallback((timeZone: string) => {
    return new Date().toLocaleTimeString("en-US", {
      timeZone: timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }, []);

  // Create marker element
  const createMarkerElement = useCallback(
    (label: ZoneLabel, currentTime: string) => {
      const element = document.createElement("div");
      element.className = "timezone-label";
      element.style.fontSize = "10px";
      element.style.fontWeight = "500";
      element.style.padding = "2px 6px";
      element.style.borderRadius = "3px";
      element.style.backgroundColor = isDarkMode
        ? "rgba(33, 33, 33, 0.85)"
        : "rgba(255, 255, 255, 0.85)";
      element.style.border = `1px solid ${
        isDarkMode
          ? ZONE_COLORS[label.tzid]
          : {
              "America/Los_Angeles": "#1976D2",
              "America/Denver": "#388E3C",
              "America/Chicago": "#FFA000",
              "America/New_York": "#D84315",
            }[label.tzid] || ZONE_COLORS[label.tzid]
      }`;
      element.style.color = isDarkMode
        ? ZONE_COLORS[label.tzid]
        : {
            "America/Los_Angeles": "#1976D2",
            "America/Denver": "#388E3C",
            "America/Chicago": "#FFA000",
            "America/New_York": "#D84315",
          }[label.tzid] || ZONE_COLORS[label.tzid];
      element.style.textAlign = "center";
      element.style.boxShadow = "0 1px 2px rgba(0,0,0,0.2)";
      element.style.minWidth = "70px";
      element.innerHTML = `<div style="font-weight: 600; margin-bottom: 1px;">${label.name}</div><div>${currentTime}</div>`;
      return element;
    },
    [ZONE_COLORS, isDarkMode]
  );

  // Update time labels
  const updateTimes = useCallback(() => {
    if (!map || !isVisible || !markerLibraryRef.current) return;

    markersRef.current.forEach((marker) => {
      const label = ZONE_LABELS.find(
        (l) => l.lat === marker.position?.lat && l.lng === marker.position?.lng
      );
      if (label) {
        const currentTime = getCurrentTime(label.tzid);
        const element = createMarkerElement(label, currentTime);
        marker.content = element;
      }
    });
  }, [map, isVisible, getCurrentTime, createMarkerElement]);

  // Setup markers
  useEffect(() => {
    const setupMarkers = async () => {
      if (!map || !isVisible) return;

      // Load marker library if not loaded
      const AdvancedMarkerElement = await loadMarkerLibrary();
      if (!AdvancedMarkerElement) return;

      // Clear existing markers
      markersRef.current.forEach((marker) => (marker.map = null));
      markersRef.current = [];

      // Create new markers
      const newMarkers = ZONE_LABELS.map((label) => {
        const currentTime = getCurrentTime(label.tzid);
        const markerElement = createMarkerElement(label, currentTime);

        const marker = new AdvancedMarkerElement({
          position: { lat: label.lat, lng: label.lng },
          map: map,
          content: markerElement,
          collisionBehavior: google.maps.CollisionBehavior.REQUIRED,
        });

        return marker;
      });

      markersRef.current = newMarkers;
    };

    setupMarkers();

    return () => {
      markersRef.current.forEach((marker) => (marker.map = null));
      markersRef.current = [];
    };
  }, [map, isVisible, loadMarkerLibrary, getCurrentTime, createMarkerElement]);

  // Setup time update interval
  useEffect(() => {
    if (!map || !isVisible) return;

    updateTimes(); // Initial update
    updateIntervalRef.current = setInterval(updateTimes, 60000);

    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, [map, isVisible, updateTimes]);

  // Setup GeoJSON layer
  useEffect(() => {
    if (!map || !isVisible) {
      if (dataLayer) {
        dataLayer.setMap(null);
      }
      return;
    }

    const layer = new google.maps.Data({ map });
    setDataLayer(layer);

    // Load GeoJSON directly from file
    layer.loadGeoJson("/us-timezones.geojson", null, () => {
      // Style the zones based on their timezone - only borders, no fill
      layer.setStyle((feature) => {
        const tzid = feature.getProperty("tzid") as string;

        return {
          fillColor: ZONE_COLORS[tzid] || "#CCCCCC",
          fillOpacity: 0, // No area coloring, only borders
          strokeColor: isDarkMode
            ? ZONE_COLORS[tzid]
            : {
                "America/Los_Angeles": "#1976D2",
                "America/Denver": "#388E3C",
                "America/Chicago": "#FFA000",
                "America/New_York": "#D84315",
              }[tzid] || "#999999",
          strokeWeight: isDarkMode ? 2 : 1.5, // Slightly thicker borders for visibility
          strokeOpacity: isDarkMode ? 0.7 : 0.8, // More visible borders
        };
      });
    });

    return () => {
      layer.setMap(null);
    };
  }, [map, isVisible, ZONE_COLORS, isDarkMode]);

  return null;
};
